// src/components/admin/page-management/section-forms/TextForm.js
import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faAlignLeft, faAlignCenter, faAlignRight, faHeading, 
  faPlus, faMinus, faFont, faPalette, faLayerGroup
} from '@fortawesome/free-solid-svg-icons';

const TextForm = ({ content, updateContent }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Font family options
  const fontFamilies = [
    { value: 'font-sans', label: 'Sans-serif', desc: 'Clean, modern look (Default)' },
    { value: 'font-serif', label: 'Serif', desc: 'Traditional, elegant style' },
    { value: 'font-mono', label: 'Monospace', desc: 'Code or technical look' },
    { value: 'font-display', label: 'Display', desc: 'Bold, attention-grabbing' },
    { value: 'font-handwriting', label: 'Handwriting', desc: 'Cursive, personal feel' }
  ];

  // Font size options
  const fontSizes = [
    { value: 'text-xs', label: 'Extra Small' },
    { value: 'text-sm', label: 'Small' },
    { value: 'text-base', label: 'Normal' },
    { value: 'text-lg', label: 'Large' },
    { value: 'text-xl', label: 'Extra Large' },
    { value: 'text-2xl', label: 'Heading 2' },
    { value: 'text-3xl', label: 'Heading 1' },
    { value: 'text-4xl', label: 'Display' },
    { value: 'text-5xl', label: 'Hero' }
  ];

  // Animation options
  const animations = [
    { value: '', label: 'None' },
    { value: 'fade-in', label: 'Fade In' },
    { value: 'slide-up', label: 'Slide Up' },
    { value: 'slide-in-left', label: 'Slide from Left' },
    { value: 'slide-in-right', label: 'Slide from Right' },
    { value: 'zoom-in', label: 'Zoom In' },
    { value: 'typing', label: 'Typing Effect' }
  ];

  // Typography style options
  const typographyStyles = [
    { value: '', label: 'Default' },
    { value: 'font-light', label: 'Light' },
    { value: 'font-normal', label: 'Regular' },
    { value: 'font-medium', label: 'Medium' },
    { value: 'font-semibold', label: 'Semi Bold' },
    { value: 'font-bold', label: 'Bold' },
    { value: 'font-extrabold', label: 'Extra Bold' },
    { value: 'italic', label: 'Italic' },
    { value: 'underline', label: 'Underlined' }
  ];

  // Text color presets
  const colorPresets = [
    { value: 'text-gray-900', label: 'Black', color: '#111827' },
    { value: 'text-gray-700', label: 'Dark Gray', color: '#374151' },
    { value: 'text-gray-500', label: 'Gray', color: '#6B7280' },
    { value: 'text-blue-600', label: 'Blue', color: '#2563EB' },
    { value: 'text-green-600', label: 'Green', color: '#059669' },
    { value: 'text-red-600', label: 'Red', color: '#DC2626' },
    { value: 'text-yellow-600', label: 'Yellow', color: '#D97706' },
    { value: 'text-purple-600', label: 'Purple', color: '#7C3AED' },
    { value: 'text-pink-600', label: 'Pink', color: '#DB2777' },
    { value: 'custom', label: 'Custom', color: content?.customTextColor || '#000000' }
  ];

  // Background color presets
  const bgColorPresets = [
    { value: '', label: 'None', color: 'transparent' },
    { value: 'bg-white', label: 'White', color: '#FFFFFF' },
    { value: 'bg-gray-50', label: 'Light Gray', color: '#F9FAFB' },
    { value: 'bg-gray-100', label: 'Gray', color: '#F3F4F6' },
    { value: 'bg-blue-50', label: 'Blue', color: '#EFF6FF' },
    { value: 'bg-green-50', label: 'Green', color: '#ECFDF5' },
    { value: 'bg-red-50', label: 'Red', color: '#FEF2F2' },
    { value: 'bg-yellow-50', label: 'Yellow', color: '#FFFBEB' },
    { value: 'bg-purple-50', label: 'Purple', color: '#F5F3FF' },
    { value: 'custom', label: 'Custom', color: content?.customBgColor || '#FFFFFF' }
  ];

  // Line height options
  const lineHeights = [
    { value: 'leading-none', label: 'None' },
    { value: 'leading-tight', label: 'Tight' },
    { value: 'leading-snug', label: 'Snug' },
    { value: 'leading-normal', label: 'Normal' },
    { value: 'leading-relaxed', label: 'Relaxed' },
    { value: 'leading-loose', label: 'Loose' }
  ];

  // Spacing options
  const spacingOptions = [
    { value: '0', label: 'None' },
    { value: '1', label: 'Extra Small' },
    { value: '2', label: 'Small' },
    { value: '4', label: 'Medium' },
    { value: '6', label: 'Large' },
    { value: '8', label: 'Extra Large' },
    { value: '12', label: 'Double' },
    { value: '16', label: 'Triple' }
  ];

  // Style (container) types
  const containerStyles = [
    { value: '', label: 'Default' },
    { value: 'card', label: 'Card with Shadow' },
    { value: 'bordered', label: 'Bordered' },
    { value: 'callout', label: 'Callout Box' },
    { value: 'quote', label: 'Quotation' },
    { value: 'highlight', label: 'Highlighted' }
  ];

  const renderColorButtons = (colors, selectedValue, updateField) => (
    <div className="flex flex-wrap gap-2 mt-2">
      {colors.map(color => (
        <button
          key={color.value}
          type="button"
          className={`w-8 h-8 rounded-full border ${selectedValue === color.value ? 'ring-2 ring-blue-500 ring-offset-2' : 'border-gray-300'}`}
          style={{ backgroundColor: color.color }}
          title={color.label}
          onClick={() => {
            updateContent(updateField, color.value);
            if (color.value === 'custom') {
              // If custom, also update the custom color value field
              const customField = updateField === 'textColor' ? 'customTextColor' : 'customBgColor';
              updateContent(customField, color.color);
            }
          }}
        ></button>
      ))}
      
      {selectedValue === 'custom' && (
        <input
          type="color"
          className="h-8 p-0 border border-gray-300 rounded"
          value={updateField === 'textColor' ? content?.customTextColor || '#000000' : content?.customBgColor || '#FFFFFF'}
          onChange={(e) => {
            const customField = updateField === 'textColor' ? 'customTextColor' : 'customBgColor';
            updateContent(customField, e.target.value);
          }}
        />
      )}
    </div>
  );

  // Generate a preview of the text with current styles
  const renderPreview = () => {
    const fontFamily = content?.fontFamily || 'font-sans';
    const fontSize = content?.fontSize || 'text-base';
    const textColor = content?.textColor === 'custom' ? 'custom-color' : content?.textColor || 'text-gray-900';
    const bgColor = content?.bgColor === 'custom' ? 'custom-bg' : content?.bgColor || '';
    const typography = content?.typography || '';
    const alignment = content?.alignment || 'left';
    const lineHeight = content?.lineHeight || 'leading-normal';
    const containerStyle = content?.containerStyle || '';

    let alignClass = '';
    if (alignment === 'left') alignClass = 'text-left';
    if (alignment === 'center') alignClass = 'text-center';
    if (alignment === 'right') alignClass = 'text-right';

    let containerClass = '';
    if (containerStyle === 'card') containerClass = 'shadow-md rounded-lg p-6';
    if (containerStyle === 'bordered') containerClass = 'border border-gray-300 rounded p-4';
    if (containerStyle === 'callout') containerClass = 'border-l-4 border-blue-500 pl-4 py-2';
    if (containerStyle === 'quote') containerClass = 'border-l-4 border-gray-300 italic pl-4 py-2';
    if (containerStyle === 'highlight') containerClass = 'bg-yellow-50 p-4 rounded';

    return (
      <div className="mt-6 p-5 border rounded bg-gray-50">
        <h3 className="font-medium text-lg mb-3">Preview</h3>
        <div 
          className={`${containerClass} ${bgColor} overflow-hidden`}
          style={content?.bgColor === 'custom' ? { backgroundColor: content.customBgColor } : {}}
        >
          <div className={`${alignClass} ${fontFamily} ${fontSize} ${typography} ${lineHeight} ${textColor}`}
            style={content?.textColor === 'custom' ? { color: content.customTextColor } : {}}
          >
            {content?.title && <h2 className={`font-bold mb-3 ${fontSize === 'text-base' ? 'text-xl' : fontSize}`}>{content.title}</h2>}
            {content?.text || <span className="text-gray-400">Your text will appear here...</span>}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <h3 className="font-bold text-lg mb-4">Text Section</h3>
      
      {/* Basic Options */}
      <div className="bg-white p-4 border rounded-md shadow-sm mb-6">
        <h4 className="font-medium text-gray-700 mb-3">Content</h4>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1 text-gray-700">Title</label>
          <input
            type="text"
            className="w-full p-2 border rounded"
            value={content?.title || ''}
            onChange={(e) => updateContent('title', e.target.value)}
            placeholder="Section Title (optional)"
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1 text-gray-700">Text Content</label>
          <textarea
            className="w-full p-2 border rounded min-h-[120px]"
            rows="5"
            value={content?.text || ''}
            onChange={(e) => updateContent('text', e.target.value)}
            placeholder="Enter your text content here..."
          ></textarea>
        </div>
      </div>
      
      {/* Styling Options */}
      <div className="bg-white p-4 border rounded-md shadow-sm mb-6">
        <div className="flex justify-between items-center mb-3">
          <h4 className="font-medium text-gray-700">Typography & Style</h4>
          <button
            type="button"
            className="text-sm text-blue-600 hover:text-blue-800"
            onClick={() => setShowAdvanced(!showAdvanced)}
          >
            {showAdvanced ? 'Hide Advanced Options' : 'Show Advanced Options'}
          </button>
        </div>
        
        {/* Alignment */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1 text-gray-700">Alignment</label>
          <div className="flex gap-2">
            <button
              type="button"
              className={`flex-1 p-2 border rounded ${content?.alignment === 'left' || !content?.alignment ? 'bg-blue-100 border-blue-400' : 'hover:bg-gray-50'}`}
              onClick={() => updateContent('alignment', 'left')}
            >
              <FontAwesomeIcon icon={faAlignLeft} className="mr-2" />
              Left
            </button>
            <button
              type="button"
              className={`flex-1 p-2 border rounded ${content?.alignment === 'center' ? 'bg-blue-100 border-blue-400' : 'hover:bg-gray-50'}`}
              onClick={() => updateContent('alignment', 'center')}
            >
              <FontAwesomeIcon icon={faAlignCenter} className="mr-2" />
              Center
            </button>
            <button
              type="button"
              className={`flex-1 p-2 border rounded ${content?.alignment === 'right' ? 'bg-blue-100 border-blue-400' : 'hover:bg-gray-50'}`}
              onClick={() => updateContent('alignment', 'right')}
            >
              <FontAwesomeIcon icon={faAlignRight} className="mr-2" />
              Right
            </button>
          </div>
        </div>
        
        {/* Font Family */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1 text-gray-700">Font Family</label>
          <select
            className="w-full p-2 border rounded"
            value={content?.fontFamily || 'font-sans'}
            onChange={(e) => updateContent('fontFamily', e.target.value)}
          >
            {fontFamilies.map(font => (
              <option key={font.value} value={font.value}>{font.label} - {font.desc}</option>
            ))}
          </select>
        </div>
        
        {/* Container Style */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1 text-gray-700">
            <FontAwesomeIcon icon={faLayerGroup} className="mr-2" />
            Container Style
          </label>
          <select
            className="w-full p-2 border rounded"
            value={content?.containerStyle || ''}
            onChange={(e) => updateContent('containerStyle', e.target.value)}
          >
            {containerStyles.map(style => (
              <option key={style.value} value={style.value}>{style.label}</option>
            ))}
          </select>
        </div>
        
        {/* Font Size */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1 text-gray-700">
            <FontAwesomeIcon icon={faFont} className="mr-2" />
            Font Size
          </label>
          <select
            className="w-full p-2 border rounded"
            value={content?.fontSize || 'text-base'}
            onChange={(e) => updateContent('fontSize', e.target.value)}
          >
            {fontSizes.map(size => (
              <option key={size.value} value={size.value}>{size.label}</option>
            ))}
          </select>
        </div>
        
        {/* Colors */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1 text-gray-700">
            <FontAwesomeIcon icon={faPalette} className="mr-2" />
            Text Color
          </label>
          {renderColorButtons(colorPresets, content?.textColor || 'text-gray-900', 'textColor')}
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1 text-gray-700">Background Color</label>
          {renderColorButtons(bgColorPresets, content?.bgColor || '', 'bgColor')}
        </div>
        
        {showAdvanced && (
          <>
            {/* Typography Style */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1 text-gray-700">Typography Style</label>
              <select
                className="w-full p-2 border rounded"
                value={content?.typography || ''}
                onChange={(e) => updateContent('typography', e.target.value)}
              >
                {typographyStyles.map(style => (
                  <option key={style.value} value={style.value}>{style.label}</option>
                ))}
              </select>
            </div>
            
            {/* Line Height */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1 text-gray-700">Line Height</label>
              <select
                className="w-full p-2 border rounded"
                value={content?.lineHeight || 'leading-normal'}
                onChange={(e) => updateContent('lineHeight', e.target.value)}
              >
                {lineHeights.map(height => (
                  <option key={height.value} value={height.value}>{height.label}</option>
                ))}
              </select>
            </div>
            
            {/* Spacing Controls */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">Top Margin</label>
                <select
                  className="w-full p-2 border rounded"
                  value={content?.marginTop || '4'}
                  onChange={(e) => updateContent('marginTop', e.target.value)}
                >
                  {spacingOptions.map(spacing => (
                    <option key={spacing.value} value={spacing.value}>{spacing.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">Bottom Margin</label>
                <select
                  className="w-full p-2 border rounded"
                  value={content?.marginBottom || '4'}
                  onChange={(e) => updateContent('marginBottom', e.target.value)}
                >
                  {spacingOptions.map(spacing => (
                    <option key={spacing.value} value={spacing.value}>{spacing.label}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Animation */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1 text-gray-700">Animation</label>
              <select
                className="w-full p-2 border rounded"
                value={content?.animation || ''}
                onChange={(e) => updateContent('animation', e.target.value)}
              >
                {animations.map(anim => (
                  <option key={anim.value} value={anim.value}>{anim.label}</option>
                ))}
              </select>
            </div>
            
            {/* Extra Features: Column Count, Letter Spacing, Word Spacing */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <details>
                <summary className="cursor-pointer text-sm font-medium text-gray-700 mb-2">Additional Text Properties</summary>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">Letter Spacing</label>
                    <select
                      className="w-full p-2 border rounded"
                      value={content?.letterSpacing || 'tracking-normal'}
                      onChange={(e) => updateContent('letterSpacing', e.target.value)}
                    >
                      <option value="tracking-tighter">Tighter</option>
                      <option value="tracking-tight">Tight</option>
                      <option value="tracking-normal">Normal</option>
                      <option value="tracking-wide">Wide</option>
                      <option value="tracking-wider">Wider</option>
                      <option value="tracking-widest">Widest</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">Text Transform</label>
                    <select
                      className="w-full p-2 border rounded"
                      value={content?.textTransform || ''}
                      onChange={(e) => updateContent('textTransform', e.target.value)}
                    >
                      <option value="">None</option>
                      <option value="uppercase">UPPERCASE</option>
                      <option value="lowercase">lowercase</option>
                      <option value="capitalize">Capitalize</option>
                    </select>
                  </div>
                </div>
                
                <div className="mt-3">
                  <label className="block text-sm font-medium mb-1 text-gray-700">Multi-Column Layout</label>
                  <select
                    className="w-full p-2 border rounded"
                    value={content?.columnCount || '1'}
                    onChange={(e) => updateContent('columnCount', e.target.value)}
                  >
                    <option value="1">Single Column</option>
                    <option value="2">Two Columns</option>
                    <option value="3">Three Columns</option>
                    <option value="4">Four Columns</option>
                  </select>
                </div>
                
                <div className="mt-3">
                  <label className="block text-sm font-medium mb-1 text-gray-700">Text Shadow</label>
                  <select
                    className="w-full p-2 border rounded"
                    value={content?.textShadow || ''}
                    onChange={(e) => updateContent('textShadow', e.target.value)}
                  >
                    <option value="">None</option>
                    <option value="text-shadow-sm">Light</option>
                    <option value="text-shadow">Medium</option>
                    <option value="text-shadow-lg">Heavy</option>
                  </select>
                </div>
              </details>
            </div>
          </>
        )}
      </div>
      
      {/* Preview Toggle */}
      <div className="text-right mb-2">
        <button
          type="button"
          className="inline-flex items-center px-3 py-1 border border-blue-300 text-sm leading-5 font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:shadow-outline-blue"
          onClick={() => setShowPreview(!showPreview)}
        >
          {showPreview ? 'Hide Preview' : 'Show Preview'}
        </button>
      </div>
      
      {/* Preview Area */}
      {showPreview && renderPreview()}
    </div>
  );
};

export default TextForm;



